<template>
  <div class="personalized-dashboard-container">
    <Navigation :pathList="navigationArray"/>
    <div class="body-container">
      <div class="personal-setting-container">
        <div class="view-container main-view-container">
          <div class="show-container">
            <span class="main view-title show">{{ $t('admin.hrDashboard.label.showInMainView') }}</span>
            <draggable
              v-bind="dragOptions"
              class="list-group"
              group="main"
              :list="mainShowViewList">
              <div
                class="list-group-item"
                v-for="element in mainShowViewList"
                :key="element.key"
              >
                <span class="iconfont icona-bianzu32beifen"></span>
                <span class="label">{{ element.type === HR_REPORT_CONFIG_DATA_TYPE.CHART ? element.i18nName : $t(element.label) }}</span>
              </div>
            </draggable>
          </div>
          <div class="hide-container">
            <span class="main view-title hide">{{ $t('admin.hrDashboard.label.hideFromMainView') }}</span>
            <draggable
              v-bind="dragOptions"
              class="list-group"
              group="main"
              :list="mainHideViewList"
            >
              <div
                class="list-group-item"
                v-for="element in mainHideViewList"
                :key="element.key"
              >
                <span class="iconfont icona-bianzu32beifen"></span>
                <span class="label">{{ element.type === HR_REPORT_CONFIG_DATA_TYPE.CHART ? element.i18nName : $t(element.label) }}</span>
              </div>
            </draggable>
          </div>
        </div>
        <div class="view-container right-view-container">
          <div class="show-container">
            <span class="right view-title show">{{ $t('admin.hrDashboard.label.showInRightView') }}</span>
            <draggable
              v-bind="dragOptions"
              class="list-group"
              group="right"
              :list="rightShowViewList">
              <div
                class="list-group-item"
                v-for="element in rightShowViewList"
                :key="element.key"
              >
                <span class="iconfont icona-bianzu32beifen"></span>
                <span class="label">{{ $t(element.label) }}</span>
              </div>
            </draggable>
          </div>
          <div class="hide-container">
            <span class="right view-title hide">{{ $t('admin.hrDashboard.label.hideFromRightView') }}</span>
            <draggable
              v-bind="dragOptions"
              class="list-group"
              group="right"
              :list="rightHideViewList">
              <div
                class="list-group-item"
                v-for="element in rightHideViewList"
                :key="element.key"
              >
                <span class="iconfont icona-bianzu32beifen"></span>
                <span class="label">{{ $t(element.label) }}</span>
              </div>
            </draggable>
          </div>
        </div>
      </div>
      <div class="button-container">
        <el-button class="button" @click="handelCancel">{{ $t('admin.hrDashboard.btn.cancel') }}</el-button>
        <el-button class="button" @click="handelSave" type="primary" :disabled="loading"><RectLoading class="loading" v-show="loading"/>{{ $t('admin.hrDashboard.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>

import _ from 'lodash'
import { mapActions } from 'vuex'
import draggable from 'vuedraggable'
import RectLoading from '@/components/loading/rectLoading.vue'
import Navigation from '../components/navigation.vue'
import mixin from './components/mixin'
import { HR_REPORT_CONFIG_DATA_TYPE } from '@/constant/common'
import { addI18nProperty } from '@/i18n'

export default {
  mixins: [mixin],
  components: {
    Navigation,
    draggable,
    RectLoading
  },
  data () {
    return {
      HR_REPORT_CONFIG_DATA_TYPE,
      navigationArray: [
        {
          i18nName: 'admin.hrDashboard.title',
          path: '/v-admin/hr-dashboard'
        },
        {
          i18nName: 'admin.hrDashboard.personalTitle',
          path: '',
          current: true
        }
      ],
      mainShowViewList: [],
      mainHideViewList: [],
      rightShowViewList: [],
      rightHideViewList: [],
      chartList: [],
      loading: false
    }
  },
  computed: {
    dragOptions () {
      return {
        animation: 200,
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    hideChartList () {
      return _.filter(this.chartList)
    }
  },
  created () {
    this.rightShowViewList = [...this.totalRightList]
    this.initPersonalSetting()
  },
  methods: {
    ...mapActions('hrDashboard', ['getPersonalizedInfo', 'savePersonalizedInfo']),
    ...mapActions('bi', ['getChartConfigList']),
    async initPersonalSetting () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadChartConfigList()
        await this.loadPageSetting()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    isBiChart (info) {
      return info.key.startsWith('bi_chart_')
    },
    async loadChartConfigList () {
      if (!this.hasHrReportPermission) {
        return
      }
      const resp = await this.getChartConfigList({ type: HR_REPORT_CONFIG_DATA_TYPE.CHART })
      if (resp && resp.data) {
        this.chartList = _.map(resp.data.data, chart => addI18nProperty({
          key: `bi_chart_${chart.id}`,
          name: chart.name,
          cn_name: chart.cn_name,
          type: chart.type
        }, 'i18nName', 'cn_name', 'name'))
      }
    },
    async loadPageSetting () {
      const resp = await this.getPersonalizedInfo()
      const config = resp?.config

      const mainHideMap = {}
      _.forEach(this.totalMainList, (item) => {
        mainHideMap[item.key] = { hide: true, item }
      })
      _.forEach(this.chartList, (item) => {
        mainHideMap[item.key] = { hide: true, item }
      })
      const mainShow = config?.mainShow || _.filter([...this.totalMainList], item => item.key !== 'recruiterKpi')
      this.mainShowViewList = _.filter(mainShow, (item) => {
        const hideInfo = mainHideMap[item.key]
        if (hideInfo) {
          hideInfo.hide = false
          if (this.isBiChart(item)) {
            addI18nProperty(item, 'i18nName', 'cn_name', 'name', hideInfo.item)
          }
        } else {
          return false
        }
        return true
      })
      this.mainHideViewList = _.chain(mainHideMap).filter({ hide: true }).map('item').value()

      const rightShowKey = _.map(config.rightShow, 'key')
      const rightShowList = []
      const rightHideList = []
      _.forEach(this.totalRightList, item => {
        if (rightShowKey.includes(item.key)) {
          rightShowList.push(item)
        } else {
          rightHideList.push(item)
        }
      })
      this.rightShowViewList = rightShowList
      this.rightHideViewList = rightHideList
    },
    handelCancel () {
      this.$router.push('/v-admin/hr-dashboard')
    },
    async handelSave () {
      this.loading = true
      try {
        const param = {
          mainShow: _.map(this.mainShowViewList, item => item.type === HR_REPORT_CONFIG_DATA_TYPE.CHART ? { key: item.key, type: item.type } : item),
          mainHide: _.filter(this.mainHideViewList, item => item.type !== HR_REPORT_CONFIG_DATA_TYPE.CHART),
          rightShow: this.rightShowViewList,
          rightHide: this.rightHideViewList
        }
        const resp = await this.savePersonalizedInfo(param)
        if (resp) {
          this.$router.push('/v-admin/hr-dashboard')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.personalized-dashboard-container {
  font-size: 14px;
  min-width: 1320px;
  overflow: auto;

  .body-container {
    background: #FFFFFF;
    border-radius: 5px;
    width: 100%;
    padding: 30px 30px 60px;

    .personal-setting-container {
      display: flex;

      .view-container {
        flex: 1;

        .view-title {
          font-size: 16px;
          line-height: 22px;
          height: 22px;
          margin-bottom: 12px;
          display: block;
          font-weight: bold;
          color: #303133;
        }

        .ghost {
          opacity: 0.5;
          background: #c8ebfb;
        }

        .list-group-item {
          padding: 16px;
          border: 1px solid #DCDFE6;
          border-bottom: 0;
          cursor: move;

          .label {
            font-size: 14px;
            line-height: 20px;
            height: 20px;
            color: #303133;
          }

          .iconfont {
            margin-right: 10px;
            font-size: 19px;
            color: #D8D8D8;
          }

          > span{
            display: inline-block;
            line-height: 20px;
            vertical-align: middle;
          }

          &:first-child {
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
          }

          &:last-child {
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
            border: 1px solid #DCDFE6;
          }
        }

        .hide-container {
          margin-top: 30px;
          .list-group-item {
            background: #F9F9F9;
          }
        }
      }

      .main-view-container {
        border-right: 1px solid #DCDFE6;
        padding-right: 80px;
      }

      .right-view-container {
        padding-left: 80px;
      }
    }

    .button-container {
      text-align: right;
      margin-top: 60px;

      .button {
        font-size: 16px;
        font-weight: bold;
        position: relative;

        &.el-button--primary {
          background-color: #0096cc;
          border-color: #FFFFFF;
        }

        .loading {
          position: absolute;
        }
      }
    }
  }
}
</style>
